import { clamp, preventDefault } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const ZOOM_IN_LEVEL = 2.5;
// Only the SPH full-screen viewer on desktop gets a gentler first zoom.
// Keep 2.5x and 5x gestures unchanged everywhere else, including mobile.
const SPH_DESKTOP_ZOOM_IN_LEVEL = 1.75;
const SPH_DESKTOP_MAX_ZOOM = 3.5;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  #swipe = null;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {boolean} */
  #mouseListening = false;
  /** @type {number | null} */
  #animationFrame = null;

  get #image() {
    return this.querySelector('img');
  }

  /**
   * Returns true when the image is zoomed in beyond the default scale.
   * @returns {boolean}
   */
  get isZoomed() {
    return this.#scale > MIN_ZOOM + 0.01;
  }

  /** Only the shared SPH product viewer on desktop (all languages). */
  get #isDesktopSphViewer() {
    return this.closest('sph-slider.sph-slider') !== null &&
      window.matchMedia('(min-width: 880px)').matches;
  }

  /** Dutch draft pilot: intentional zoom controls/pinch only, on every device. */
  get #explicitSphZoom() {
    return this.closest('sph-slider[data-sph-explicit-zoom]') !== null;
  }

  /**
   * Zooms in to ZOOM_IN_LEVEL and resets translation to center.
   */
  zoomIn() {
    this.#scale = this.#isDesktopSphViewer ? SPH_DESKTOP_ZOOM_IN_LEVEL : ZOOM_IN_LEVEL;
    this.#translate = { x: 0, y: 0 };
    this.#requestUpdateTransform();
  }

  /**
   * Zooms out to default scale and resets translation.
   */
  zoomOut() {
    this.#scale = DEFAULT_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#requestUpdateTransform();
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    // Image clicks (including synthetic clicks after touch/pan) do not zoom or close.
    // Capture also protects against delegated image handlers added by the theme.
    this.addEventListener('click', this.#handleImageClick, { capture: true, signal });
    this.addEventListener('dblclick', this.#handleImageClick, { capture: true, signal });

    // Touch events (mobile pinch & drag)
    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('touchcancel', this.#handleTouchCancel, options);

    // Desktop drag + wheel zoom, only active in the SPH full-screen viewer.
    this.addEventListener('mousedown', this.#handleMouseDown, { signal });
    this.addEventListener('wheel', this.#handleDesktopSphWheel, { passive: false, signal });

    // Pointer events: stop propagation when zoomed so the slideshow doesn't
    // interpret the touch as a swipe gesture (slideshow uses pointerdown on its scroller)
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });

    this.#image?.addEventListener('load', this.#updateTransform, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /** @param {MouseEvent} event */
  #handleImageClick = (event) => {
    if (!this.#explicitSphZoom) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    this.#swipe = null;
    if (event.touches.length === 2) {
      // Pinch-zoom: always intercept
      preventDefault(event);
      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;
      this.#startZoomGesture(point1, point2);
    } else if (event.touches.length === 1) {
      if (this.#scale > MIN_ZOOM + 0.01) {
        // Zoomed in: intercept for drag/pan — stop propagation to prevent
        // the slideshow's mousedown listener from initiating swipe navigation
        preventDefault(event);
        event.stopPropagation();
        const point = touchToPoint(event.touches[0]);
        if (!point) return;
        this.#startDragGesture(point);
      }
      else if (this.#explicitSphZoom) {
        preventDefault(event);
        event.stopPropagation();
        const touch = event.touches[0];
        const slider = this.closest('sph-slider');
        this.#swipe = { x: touch.clientX, y: touch.clientY, id: touch.identifier,
          index: slider.currentIndex };
      }
    }
  };

  /**
   * Start a zoom gesture with two points
   * @param {Point} point1
   * @param {Point} point2
   */
  #startZoomGesture(point1, point2) {
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single point
   * @param {Point} point
   */
  #startDragGesture(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (this.#swipe) {
      preventDefault(event);
      event.stopPropagation();
    }
    const isZooming = event.touches.length === 2;
    const isDragging = event.touches.length === 1 && this.#isDragging;

    if (isZooming) {
      // Pinch-zoom: intercept and process
      preventDefault(event);
      this.#processZoomGesture(event);
    } else if (isDragging) {
      // Drag/pan when zoomed: intercept and process
      preventDefault(event);
      this.#processDragGesture(event);
    }
    // Otherwise: pass through to slideshow for swipe navigation
  };

  /**
   * Process zoom gesture from touch event
   * @param {TouchEvent} event
   */
  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2) return;

    const currentMidpoint = getMidpoint(point1, point2);
    const currentDistance = getDistance(point1, point2);
    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    const rect = this.getBoundingClientRect();
    const distanceFromCenter = {
      x: currentMidpoint.x - containerCenter.x - (this.#explicitSphZoom ? rect.left : 0),
      y: currentMidpoint.y - containerCenter.y - (this.#explicitSphZoom ? rect.top : 0),
    };

    // Calculate how the image position needs to change to keep the midpoint stationary
    // The correction factor accounts for the change in scale
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch event
   * @param {TouchEvent} event
   */
  #processDragGesture(event) {
    const point = touchToPoint(event.touches[0]);
    if (!point) return;

    // Calculate new translation
    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchCancel = () => {
    this.#swipe = null;
    this.#isDragging = false;
    this.#requestUpdateTransform();
  };

  #handleTouchEnd = (event) => {
    const swipe = this.#swipe;
    this.#swipe = null;
    if (swipe && event.touches.length === 0 && !this.isZoomed) {
      preventDefault(event);
      event.stopPropagation();
      const touch = Array.from(event.changedTouches).find(t => t.identifier === swipe.id);
      if (touch) {
        const dx = touch.clientX - swipe.x;
        const dy = touch.clientY - swipe.y;
        if (Math.abs(dx) >= 30 && Math.abs(dx) > Math.abs(dy)) {
          const slider = this.closest('sph-slider');
          const next = Math.max(0, Math.min(slider.slideCount - 1, swipe.index + (dx < 0 ? 1 : -1)));
          slider.goToSlide(next);
        }
      }
    }
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
    }
  };

  /**
   * Stops the pointerdown event from bubbling to the slideshow when the image is zoomed,
   * preventing the slideshow from starting swipe navigation during a pan gesture.
   * Only applies to touch input; mouse drag is handled separately via #handleMouseDown.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (this.isZoomed && event.pointerType === 'touch') {
      event.stopPropagation();
    }
  };

  /**
   * Desktop SPH only: gently zoom toward the pointer using the mouse wheel.
   * Wheel outside the actual image still behaves normally. No mobile,
   * other dialogs or regular product-gallery behaviour is altered.
   * @param {WheelEvent} event
   */
  #handleDesktopSphWheel = (event) => {
    if (this.#explicitSphZoom || !this.#isDesktopSphViewer || this.#isDragging || !event.deltaY) return;
    const dialog = this.closest('sph-slider.sph-slider')?.querySelector('dialog[open]');
    if (!dialog) return;

    const normalizedDelta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 600 : 1);
    const cappedDelta = clamp(normalizedDelta, -240, 240);
    const originalScale = this.#scale;
    const nextScale = clamp(
      originalScale * Math.exp(-cappedDelta * 0.0018),
      MIN_ZOOM,
      SPH_DESKTOP_MAX_ZOOM
    );

    // Avoid browser/underlying-page scrolling or Ctrl+wheel browser zoom.
    event.preventDefault();
    event.stopPropagation();
    if (Math.abs(nextScale - originalScale) < 0.0001) return;

    // CSS transform is scale(s) translate(x,y). Keep the image point under
    // the cursor stationary while zooming in or out.
    const rect = this.getBoundingClientRect();
    const cursorX = event.clientX - (rect.left + rect.width / 2);
    const cursorY = event.clientY - (rect.top + rect.height / 2);
    this.#translate.x += cursorX * (1 / nextScale - 1 / originalScale);
    this.#translate.y += cursorY * (1 / nextScale - 1 / originalScale);
    this.#scale = nextScale;
    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0) return; // alleen linkermuisknop
    if (!this.isZoomed) return; // alleen slepen wanneer ingezoomd
    if (this.#mouseListening) return; // voorkom dubbele listeners

    event.preventDefault();
    event.stopPropagation(); // prevent slideshow's mousedown listener from starting swipe navigation
    const point = mouseToPoint(event);
    this.#startDragGesture(point);
    this.classList.add('drag-zoom-wrapper--dragging');

    this.#mouseListening = true;
    window.addEventListener('mousemove', this.#handleMouseMove);
    window.addEventListener('mouseup', this.#handleMouseUp, { once: true });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;
    event.preventDefault();
    const point = mouseToPoint(event);

    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  };

  #handleMouseUp = () => {
    this.#isDragging = false;
    this.#mouseListening = false;
    this.classList.remove('drag-zoom-wrapper--dragging');
    window.removeEventListener('mousemove', this.#handleMouseMove);
    this.#requestUpdateTransform();
  };

  /**
   * Get the minimum zoom for the image
   * @returns {number | null}
   */
  #getMinZoom = () => {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) return null;
    const { width: imageWidth, height: imageHeight } = imageDimensions;

    // Calculate minimum zoom to make image fit container
    // Add small buffer to avoid showing whitespace at edges
    return Math.max(containerWidth / imageWidth, containerHeight / imageHeight) + 0.001;
  };

  /**
   * Get the dimensions of the image
   * @returns {{ width: number, height: number } | null}
   */
  #getImageDimensions = () => {
    const containerRect = this.getBoundingClientRect();
    if (!this.#image) return null;
    const { naturalWidth, naturalHeight } = this.#image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth, imageHeight;
    if (naturalAspect > containerAspect) {
      // Image is wider than container (relative to height)
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      // Image is taller than container (relative to width)
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }

    if (imageWidth === 0 || imageHeight === 0) return null;

    return { width: imageWidth, height: imageHeight };
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight) return;

    this.#scale = clamp(
      this.#scale, MIN_ZOOM, this.#isDesktopSphViewer ? SPH_DESKTOP_MAX_ZOOM : MAX_ZOOM
    );

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;

    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;

    // Calculate how much the image extends beyond container
    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    const maxTranslateX = excessWidth / (2 * this.#scale);
    const maxTranslateY = excessHeight / (2 * this.#scale);

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.classList.toggle('drag-zoom-wrapper--zoomed', this.isZoomed);
    this.dispatchEvent(new CustomEvent('drag-zoom:change', { bubbles: true, detail: { scale: this.#scale } }));
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
    window.removeEventListener('mousemove', this.#handleMouseMove);
    window.removeEventListener('mouseup', this.#handleMouseUp);
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * Convert a Touch object to a Point object
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

/**
 * Convert a MouseEvent to a Point object
 * @param {MouseEvent} event
 * @returns {Point}
 */
function mouseToPoint(event) {
  return { x: event.clientX, y: event.clientY };
}

/**
 * Calculate the distance between two points
 * @param {Point} point1 - First point
 * @param {Point} point2 - Second point
 * @returns {number} The distance between the points
 */
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate the midpoint between two points
 * @param {Point} point1 - First point
 * @param {Point} point2 - Second point
 * @returns {Point} The midpoint
 */
function getMidpoint(point1, point2) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}

